/**
 * EngramKeyModal — standalone connect modal for Engram memory, same pattern
 * as GitHubCredModal.jsx. Reuses the same POST /api/engram/connect-key call
 * IntegrationsPage.jsx's EngramCard already uses (that card's engramAPI
 * wrapper is a local, unexported object, so this calls the endpoint
 * directly rather than importing something that isn't exported).
 */
import React, { useState } from "react";
import { Brain, X, Eye, EyeOff, Check, Loader2 } from "lucide-react";
import toast from "react-hot-toast";

function authHeaders() {
  const token = localStorage.getItem("authToken") || localStorage.getItem("token");
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
}

export default function EngramKeyModal({ onClose, onSaved }) {
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!apiKey.trim()) { toast.error("Paste your Engram API key first."); return; }
    setSaving(true);
    try {
      const API_BASE = process.env.REACT_APP_API_URL || "";
      const r = await fetch(`${API_BASE}/api/engram/connect-key`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ apiKey: apiKey.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || "Failed to connect Engram");
      toast.success("Engram memory connected!");
      onSaved({});
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "var(--lyra-color-surface)", borderRadius: 20, width: "100%", maxWidth: 440, boxShadow: "0 24px 60px rgba(0,0,0,0.25)", overflow: "hidden" }}>
        <div style={{ background: "linear-gradient(135deg,var(--lyra-color-primary),#4C1D95)", padding: "20px 24px", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Brain size={22} color="var(--app-on-accent)" /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontWeight: 700, fontSize: 16, color: "var(--app-on-accent)" }}>Connect Engram Memory</p>
            <p style={{ margin: "2px 0 0", fontSize: 12, color: "rgba(255,255,255,0.8)" }}>Persistent cross-session memory for your agents</p>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.15)", border: "none", cursor: "pointer", borderRadius: 8, padding: 6, display: "flex", flexShrink: 0 }}><X size={16} color="var(--app-on-accent)" /></button>
        </div>
        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label style={{ display: "block", fontWeight: 600, fontSize: 13, color: "var(--app-text-2)", marginBottom: 5 }}>Engram API Key</label>
            <div style={{ position: "relative" }}>
              <input
                type={showKey ? "text" : "password"}
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleSave()}
                placeholder="egm_…"
                style={{ width: "100%", boxSizing: "border-box", padding: "9px 36px 9px 12px", border: "1.5px solid var(--lyra-color-border)", borderRadius: 9, fontSize: 13, outline: "none", fontFamily: "inherit" }}
              />
              <button type="button" onClick={() => setShowKey(v => !v)} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--app-text-3)", padding: 0 }}>
                {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
          <button onClick={handleSave} disabled={saving || !apiKey.trim()} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, border: "none", background: apiKey.trim() ? "var(--lyra-color-primary)" : "var(--lyra-color-border)", color: apiKey.trim() ? "var(--lyra-color-surface)" : "var(--app-text-3)", borderRadius: 10, padding: "11px 0", fontWeight: 700, fontSize: 14, cursor: apiKey.trim() ? "pointer" : "not-allowed", fontFamily: "inherit" }}>
            {saving ? <><Loader2 size={15} style={{ animation: "spin 0.7s linear infinite" }} />Connecting…</> : <><Check size={15} />Connect Engram</>}
          </button>
        </div>
      </div>
    </div>
  );
}
